import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import {
  Button,
  Card,
  Col,
  Input,
  Row,
  Select,
  Typography,
  message,
  Spin
} from "antd";
import axios from "axios";
import { PRIMARY_RED } from "constants/colors";
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { setBreadcrumbItems } from "store/actions";
import { debounce } from "lodash";

const { Option } = Select;
const { Text } = Typography;

const AddEmployee = (props) => {
  const navigate = useNavigate();
  const authUser = JSON.parse(localStorage.getItem("authUser"));
  const token = authUser?.token;

  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [designation, setDesignation] = useState("");
  const [email, setEmail] = useState("");
  const [contact, setContact] = useState("");
  const [addressId, setAddressId] = useState("");
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [regionalManagerId, setRegionalManagerId] = useState("");

  const [designationList, setDesignationList] = useState([]);
  const [addressList, setAddressList] = useState([]);
  const [regionalManagerList, setRegionalManagerList] = useState([]);

  // New State/City Logic
  const [stateList, setStateList] = useState([]);
  const [cityOptions, setCityOptions] = useState([]); // API-driven options
  const [fetchingCity, setFetchingCity] = useState(false);

  const breadcrumbItems = [
    { title: "Employee", link: "#" },
    { title: "Add Employee", link: "#" },
  ];

  useEffect(() => {
    if (token) {
      fetchDropdowns();
    } else {
      message.error("Session expired, please login again");
    }
    props.setBreadcrumbItems("Employee", breadcrumbItems);

    // Hardcoded list of Indian States (Replaces JSON file)
    const states = [
      "Andaman and Nicobar Islands", "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
      "Chandigarh", "Chhattisgarh", "Dadra and Nagar Haveli", "Daman and Diu", "Delhi", "Goa",
      "Gujarat", "Haryana", "Himachal Pradesh", "Jammu and Kashmir", "Jharkhand", "Karnataka",
      "Kerala", "Ladakh", "Lakshadweep", "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
      "Mizoram", "Nagaland", "Odisha", "Puducherry", "Punjab", "Rajasthan", "Sikkim", "Tamil Nadu",
      "Telangana", "Tripura", "Uttar Pradesh", "Uttarakhand", "West Bengal"
    ];
    setStateList(states);

  }, [token]);

  const fetchDropdowns = async () => {
    try {
      const [designationRes, addressRes] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_BASEURL}/designation`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${process.env.REACT_APP_API_BASEURL}/address`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setDesignationList(designationRes.data || []);
      setAddressList(addressRes.data || []);
      setRegionalManagerList([]);
    } catch (err) {
      console.error('Dropdown fetch error:', err);
      message.error("Failed to load dropdown data");
    }
  };

  // City Search (Photon API)
  const fetchCityList = async (value) => {
    if (!value) return;
    setFetchingCity(true);
    setCityOptions([]);
    try {
      const query = `${value}, ${state}`;
      const res = await axios.get(`https://photon.komoot.io/api/`, {
        params: { q: query, limit: 10, lang: 'en' }
      });

      const features = res.data.features || [];
      const options = features.map(item => {
        const props = item.properties;
        const name = props.name;
        const placeState = props.state || '';
        const district = props.district || props.city || '';
        const label = `${name}${district ? `, ${district}` : ''}${placeState ? `, ${placeState}` : ''}`;
        return { label, value: name };
      });

      const uniqueOptions = [...new Map(options.map(item => [item.label, item])).values()];
      setCityOptions(uniqueOptions);

    } catch (e) {
      console.error(e);
    } finally {
      setFetchingCity(false);
    }
  };

  const debouncedFetchCity = useMemo(() => debounce(fetchCityList, 400), [state]);

  const handleStateChange = async (selectedState) => {
    setState(selectedState);
    setCity("");
    setCityOptions([]);
    setRegionalManagerId("");
    if (selectedState) {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASEURL}/regional-manager?state=${encodeURIComponent(selectedState)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setRegionalManagerList(res.data || []);
      } catch (err) {
        message.error("Failed to load regional managers for the selected state");
      }
    } else {
      setRegionalManagerList([]);
    }
  };

  const handleSubmit = async () => {
    if (
      !name ||
      !code ||
      !password ||
      !designation ||
      (email && !/\S+@\S+\.\S+/.test(email)) || // Email is required below but validated if present? 
      !contact || // Code says !email is required
      !addressId ||
      !state ||
      !city
    ) {
      if (!email) return message.warning("Please fill all required fields.");
    }
    // Re-checking stricter validation from original code
    if (!name || !code || !password || !designation || !email || !contact || !addressId || !state || !city) {
      return message.warning("Please fill all required fields.");
    }

    if (!/^\d{10}$/.test(contact)) {
      return message.warning("Please enter a valid 10-digit phone number.");
    }

    if (!/\S+@\S+\.\S+/.test(email)) {
      return message.warning("Please enter a valid email address.");
    }

    try {
      const payload = {
        employeeName: name,
        employeeCode: code,
        password,
        designation,
        email,
        contactNumber: contact,
        address: addressId,
        state,
        city,
        ...(regionalManagerId ? { regionalManager: regionalManagerId } : {}),
      };

      await axios.post(`${process.env.REACT_APP_API_BASEURL}/employees`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      message.success("Employee added successfully");
      navigate("/employee-listing");
    } catch (err) {
      message.error("Failed to add employee");
    }
  };

  return (
    <div className="p-4">
      <Card title="Add Employee">
        <Row gutter={16}>
          <Col span={8}>
            <Text strong>Employee Name</Text>
            <Input placeholder="Mark" value={name} onChange={(e) => setName(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Employee Code</Text>
            <Input placeholder="Otto" value={code} onChange={(e) => setCode(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Password</Text>
            <Input.Password placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8}>
            <Text strong>Designation</Text>
            <Select
              placeholder="Select..."
              value={designation}
              onChange={setDesignation}
              style={{ width: "100%" }}
              showSearch
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {designationList.map((d) => (
                <Option key={d._id} value={d._id}>
                  {d.designation}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>Email</Text>
            <Input placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} />
          </Col>
          <Col span={8}>
            <Text strong>Contact Number</Text>
            <Input placeholder="Contact Number" value={contact} onChange={(e) => setContact(e.target.value)} />
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8}>
            <Text strong>Address</Text>
            <Select showSearch placeholder="Select..." value={addressId} onChange={setAddressId} style={{ width: "100%" }}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}>
              {addressList.map((a) => (
                <Option key={a._id} value={a._id}>
                  {a.addressDetails}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>State</Text>
            <Select
              showSearch
              placeholder="Select State"
              value={state}
              onChange={handleStateChange}
              style={{ width: "100%" }}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {stateList.map((s) => (
                <Option key={s} value={s}>
                  {s}
                </Option>
              ))}
            </Select>
          </Col>
          <Col span={8}>
            <Text strong>City (Type to search)</Text>
            <Select
              showSearch
              placeholder={state ? "Start typing to find City..." : "Select State first"}
              value={city}
              onSearch={debouncedFetchCity}
              onChange={setCity}
              style={{ width: "100%" }}
              disabled={!state}
              filterOption={false}
              notFoundContent={fetchingCity ? <Spin size="small" /> : "Type to search..."}
            >
              {cityOptions.map((c) => (
                <Option key={c.value} value={c.value}>
                  {c.label}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row gutter={16} style={{ marginTop: 20 }}>
          <Col span={8}>
            <Text strong>Regional Manager</Text>
            <Select
              allowClear
              showSearch
              placeholder={state ? "Select..." : "Select State first"}
              value={regionalManagerId}
              onChange={setRegionalManagerId}
              style={{ width: "100%" }}
              disabled={!state}
              filterOption={(input, option) => option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0}
            >
              {regionalManagerList.map((rm) => (
                <Option key={rm._id} value={rm._id}>
                  {rm.employeeName}
                </Option>
              ))}
            </Select>
          </Col>
        </Row>

        <Row>
          <Col>
            <Button
              type="primary"
              htmlType="submit"
              icon={<PlusOutlined />}
              style={{ backgroundColor: PRIMARY_RED, borderColor: PRIMARY_RED, marginTop: 20 }}
              onClick={handleSubmit}
            >
              Add Employee
            </Button>
            <Button
              type="link"
              style={{ color: PRIMARY_RED, marginLeft: 10 }}
              icon={<ArrowLeftOutlined />}
              onClick={() => navigate("/employee-listing")}
            >
              Back
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(AddEmployee);